import * as React from "react";
import { cn } from "@/lib/utils";
import { riskLabel, type RiskBand } from "@/lib/copy";

/* ENSSA cardinal rule: risk = colour + label, ALWAYS together.
   This component owns that pairing. Do not render risk via a
   bare colour swatch elsewhere — go through <RiskBadge /> so the
   label is guaranteed present and the vocabulary toggle works. */

const riskClasses: Record<RiskBand, { bg: string; fg: string; dot: string }> = {
  low: {
    bg: "bg-risk-low-bg",
    fg: "text-risk-low-fg",
    dot: "bg-risk-low",
  },
  moderate: {
    bg: "bg-risk-moderate-bg",
    fg: "text-risk-moderate-fg",
    dot: "bg-risk-moderate",
  },
  high: {
    bg: "bg-risk-high-bg",
    fg: "text-risk-high-fg",
    dot: "bg-risk-high",
  },
  more_info: {
    bg: "bg-risk-insufficient-bg",
    fg: "text-risk-insufficient-fg",
    dot: "bg-risk-insufficient border border-dashed border-risk-insufficient-fg",
  },
};

interface RiskBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  band: RiskBand;
  /** Override the auto-vocabulary label (rare). */
  label?: string;
  size?: "sm" | "md";
}

export const RiskBadge = React.forwardRef<HTMLSpanElement, RiskBadgeProps>(
  ({ band, label, size = "md", className, ...props }, ref) => {
    const styles = riskClasses[band];
    const text = label ?? riskLabel(band);
    const sizeClasses =
      size === "sm" ? "text-xs px-2 py-0.5 gap-1.5" : "text-sm px-2.5 py-1 gap-2";

    return (
      <span
        ref={ref}
        role="status"
        aria-label={`Risk level: ${text}`}
        className={cn(
          "inline-flex items-center rounded-full font-bold border border-transparent",
          styles.bg,
          styles.fg,
          sizeClasses,
          className,
        )}
        {...props}
      >
        <span aria-hidden="true" className={cn("h-2.5 w-2.5 rounded-full shrink-0", styles.dot)} />
        <span>{text}</span>
      </span>
    );
  },
);
RiskBadge.displayName = "RiskBadge";
